export const RubricScoringMode = Object.freeze({
    SCORED: 'scored',
    UNSCORED: 'unscored',
});

export const CompletionStatus = Object.freeze({
    COMPLETE: 'complete',
    INCOMPLETE: 'incomplete',
    EXCUSED: 'excused',
});

const completionStatuses = Object.values(CompletionStatus);

export const normalizeCompletionStatus = (value, fallback = null) => {
    const normalizedValue = typeof value === 'string' ? value.toLowerCase() : null;
    return completionStatuses.includes(normalizedValue) ? normalizedValue : fallback;
};

export const getEffectiveCompletionStatus = ({ criteria = [], selections = {}, status }) => {
    const explicitStatus = normalizeCompletionStatus(status);
    if (explicitStatus) return explicitStatus;

    const hasAllSelections =
        criteria.length > 0 &&
        criteria.every(({ id }) => {
            const selection = selections?.[id];
            return selection !== null && selection !== undefined && selection !== '';
        });

    return hasAllSelections ? CompletionStatus.COMPLETE : null;
};

const normalizeRate = (rate) => {
    if (rate === null || rate === undefined || rate === '') return null;
    return `${rate}`;
};

const withStableLevels = (options, { sortByRate = true } = {}) => {
    const sortedOptions = [...options].sort((a, b) => {
        if (!sortByRate) return a.displayOrder - b.displayOrder;
        if (a.rate === null && b.rate === null) return a.displayOrder - b.displayOrder;
        if (a.rate === null) return 1;
        if (b.rate === null) return -1;

        const rateDifference = Number(b.rate) - Number(a.rate);
        return Number.isFinite(rateDifference) && rateDifference !== 0
            ? rateDifference
            : a.displayOrder - b.displayOrder;
    });

    return sortedOptions.map(({ displayOrder, ...option }, index) => ({
        ...option,
        level: sortedOptions.length - index - 1,
    }));
};

const toId = (id, fallback) => (id === null || id === undefined ? fallback : `${id}`);

export const normalizeRubricText = (html = '') => {
    if (!html) return '';

    const plainText = `${html}`
        .replace(/&nbsp;/gi, ' ')
        .replace(/\u00a0/g, ' ')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|section|article|li|ul|ol|h[1-6])\s*>/gi, '\n\n')
        .replace(/<li[^>]*>/gi, '')
        .replace(/<[^>]+>/g, '')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n+/g, '\n')
        .replace(/\s+/g, ' ')
        .replace(/([.!?])([A-Za-z])/g, '$1 $2')
        .trim();

    if (typeof document === 'undefined') return plainText;

    const textarea = document.createElement('textarea');
    textarea.innerHTML = plainText;
    return textarea.value;
};

export const isUnscoredRubric = (assignmentOrRubric) => {
    if (!assignmentOrRubric) return false;

    const normalizedRubric = assignmentOrRubric.rubric ?? assignmentOrRubric;
    const apiData = assignmentOrRubric.api_data ?? assignmentOrRubric;

    if (normalizedRubric?.scoring_mode) {
        return normalizedRubric.scoring_mode === RubricScoringMode.UNSCORED;
    }

    return (
        apiData?.rubric_settings?.hide_points === true ||
        apiData?.Assessment?.Rubrics?.[0]?.ScoringMethod === 0 ||
        assignmentOrRubric.ScoringMethod === 0
    );
};

export const normalizeRubricScoringMode = (assignmentOrRubric) => {
    if (!assignmentOrRubric) return assignmentOrRubric;

    const rubric = Array.isArray(assignmentOrRubric.criteria) ? assignmentOrRubric : assignmentOrRubric.rubric;
    if (!rubric) return rubric;

    return isUnscoredRubric(assignmentOrRubric) ? { ...rubric, scoring_mode: RubricScoringMode.UNSCORED } : rubric;
};

export const changeRubricScoringMode = (rubric, scoringMode) => ({
    ...rubric,
    scoring_mode: scoringMode,
    ...(scoringMode === RubricScoringMode.SCORED && {
        criteria: (rubric?.criteria ?? []).map((criterion) => ({
            ...criterion,
            options: (criterion.options ?? []).map((option) => ({
                ...option,
                rate: option.rate ?? '',
            })),
        })),
    }),
});

export const usesCompleteIncompleteGrading = (assignment) => {
    const gradingType = assignment?.api_data?.grading_type ?? assignment?.grading_type;
    return gradingType === 'pass_fail';
};

export const getRubricOptionLevel = (criterion, optionOrId) => {
    const options = criterion?.options ?? [];
    const option =
        typeof optionOrId === 'object' && optionOrId !== null
            ? optionOrId
            : options.find(({ id }) => `${id}` === `${optionOrId}`);

    const optionIndex = options.findIndex(
        (candidate) => candidate === option || (option?.id !== undefined && `${candidate.id}` === `${option.id}`)
    );
    if (optionIndex === -1) return null;

    const hasCanonicalExplicitLevels = options.every((candidate, index) => {
        const level = Number(candidate?.level);
        return (
            candidate?.level !== null &&
            candidate?.level !== undefined &&
            Number.isInteger(level) &&
            level === options.length - index - 1
        );
    });

    return hasCanonicalExplicitLevels ? Number(option.level) : options.length - optionIndex - 1;
};

export const hasAllLowestRubricRatings = (criteria = [], selections = {}) => {
    if (!criteria.length) return false;

    return criteria.every((criterion) => {
        const optionId = selections[criterion.id];
        return Boolean(optionId) && getRubricOptionLevel(criterion, optionId) === 0;
    });
};

export const shouldShowCompletionReviewWarning = ({ criteria, isGraded, selections, usesCompletionStatus }) =>
    Boolean(usesCompletionStatus && !isGraded && hasAllLowestRubricRatings(criteria, selections));

export const normalizeRubricCriteriaLevels = (criteria = []) =>
    criteria.map((criterion) => ({
        ...criterion,
        options: (criterion.options ?? []).map((option, index, options) => ({
            ...option,
            level: options.length - index - 1,
        })),
    }));

export const prepareUnscoredRubricCriteria = (criteria = [], sort = 'asc') => {
    const direction = sort === 'desc' ? -1 : 1;
    const sortedCriteria = criteria.map((criterion) => ({
        ...criterion,
        options: [...(criterion.options ?? [])].sort(
            (a, b) =>
                direction * `${a.title ?? ''}`.localeCompare(`${b.title ?? ''}`, undefined, { sensitivity: 'base' })
        ),
    }));

    return normalizeRubricCriteriaLevels(sortedCriteria);
};

export const convertCanvasRubric = (criteria, settings = {}) => {
    const normalizedCriteria = (criteria ?? []).map((criterion, criterionIndex) => {
        const options = (criterion?.ratings ?? []).map((rating, optionIndex) => ({
            id: toId(rating?.id, `canvas-rating-${criterionIndex + 1}-${optionIndex + 1}`),
            title: rating?.description ?? '',
            description: normalizeRubricText(rating?.long_description ?? ''),
            rate: normalizeRate(rating?.points),
            displayOrder: optionIndex,
        }));

        return {
            id: toId(criterion?.id, `canvas-criterion-${criterionIndex + 1}`),
            title: criterion?.description ?? '',
            description: normalizeRubricText(criterion?.long_description ?? ''),
            ignore_for_scoring: criterion?.ignore_for_scoring === true,
            options: withStableLevels(options, { sortByRate: settings?.hide_points !== true }),
        };
    });

    return {
        title: settings?.title ?? '',
        criteria: normalizedCriteria,
        sort: 'desc',
        scoring_mode: settings?.hide_points === true ? RubricScoringMode.UNSCORED : RubricScoringMode.SCORED,
    };
};

export const convertD2LRubric = (rubric = {}) => {
    const criteria = (rubric?.CriteriaGroups ?? []).flatMap((criteriaGroup, criteriaGroupIndex) => {
        const levels = criteriaGroup?.Levels ?? [];

        return (criteriaGroup?.Criteria ?? []).map((criterion, criterionIndex) => {
            const options = (criterion?.Cells ?? []).map((cell, optionIndex) => {
                const level = levels.find(({ Id }) => `${Id}` === `${cell?.LevelId}`) ?? levels[optionIndex];
                const points = rubric?.ScoringMethod === 3 ? cell?.Points : level?.Points;
                const levelId = cell?.LevelId ?? level?.Id;
                const description =
                    rubric?.ScoringMethod === 0
                        ? cell?.Feedback?.Text || cell?.Description?.Text || ''
                        : cell?.Description?.Text || cell?.Feedback?.Text || '';

                return {
                    id: toId(
                        criterion?.Id === null ||
                            criterion?.Id === undefined ||
                            levelId === null ||
                            levelId === undefined
                            ? null
                            : `${criterion.Id}-${levelId}`,
                        `d2l-rating-${criteriaGroupIndex + 1}-${criterionIndex + 1}-${optionIndex + 1}`
                    ),
                    title: level?.Name ?? '',
                    description,
                    rate: normalizeRate(points),
                    displayOrder: optionIndex,
                };
            });

            return {
                id: toId(criterion?.Id, `d2l-criterion-${criteriaGroupIndex + 1}-${criterionIndex + 1}`),
                title: criterion?.Name ?? '',
                description: '',
                options: withStableLevels(options, { sortByRate: rubric?.ScoringMethod !== 0 }),
            };
        });
    });

    return {
        title: rubric?.Name ?? '',
        criteria,
        sort: 'desc',
        scoring_mode: rubric?.ScoringMethod === 0 ? RubricScoringMode.UNSCORED : RubricScoringMode.SCORED,
    };
};
